import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, Loading, StatusBadge, EmptyState } from "@/components/common";
import BackNav from "@/components/BackNav";
import CemeteryMap from "@/components/CemeteryMap";

export default function TombaDettaglio() {
  const { id } = useParams();
  const [grave, setGrave] = useState(null);

  useEffect(() => {
    api.get(`/customer/graves/${id}`).then((r) => setGrave(r.data)).catch(() => {});
  }, [id]);

  if (!grave) return <Loading />;

  const typeLabel = { lapide: "Lapide", loculo: "Loculo", cappella: "Cappella" }[grave.grave_type] || grave.grave_type;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="grave-detail">
      <BackNav />

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-1">{typeLabel}</p>
          <h1 className="font-serif text-4xl text-navy">{grave.deceased_name} {grave.deceased_surname}</h1>
          <p className="text-sm text-muted-soft mt-1">
            {grave.cemetery?.name} — {grave.cemetery?.comune} · Campo {grave.campo || "—"}, fila {grave.fila || "—"}, n. {grave.numero || "—"}
          </p>
          {grave.position_confirmed && (
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2f5d3e] bg-[#e8f0eb] rounded-full px-3 py-1 mt-3" data-testid="position-confirmed">
              <BadgeCheck size={14} /> Posizione confermata dal professionista
            </p>
          )}
        </div>
        <Link to={`/checkout?type=singola`} data-testid="request-intervention" className="btn-primary !py-2.5 text-sm">Richiedi un intervento</Link>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="card-soft p-5">
          <p className="eyebrow mb-3">Fotografia del luogo</p>
          {grave.photo ? (
            <img src={grave.photo} alt="Luogo" className="w-full rounded-xl object-cover aspect-[4/3]" data-testid="grave-photo" />
          ) : (
            <div className="w-full aspect-[4/3] rounded-xl bg-[#f0f7ff] flex items-center justify-center text-sm text-muted-soft">Nessuna fotografia</div>
          )}
          {grave.notes && <p className="text-sm text-body mt-3">Note: {grave.notes}</p>}
        </div>
        <div className="card-soft p-5">
          <p className="eyebrow mb-3">Posizione approssimativa</p>
          <CemeteryMap image={grave.cemetery?.aerial_image} x={grave.map_x} y={grave.map_y} readOnly label={false} />
          {grave.cemetery?.notes && <p className="text-xs text-muted-soft mt-3">{grave.cemetery.notes}</p>}
        </div>
      </div>

      <section data-testid="grave-history">
        <h2 className="font-serif text-2xl text-navy mb-4">Storico interventi</h2>
        {grave.interventions.length === 0 ? (
          <EmptyState title="Nessun intervento ancora" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {grave.interventions.map((i) => (
              <Link key={i.id} to={`/area-cliente/interventi/${i.id}`} data-testid={`grave-intervention-${i.id}`} className="card-soft p-5 block">
                <div className="flex justify-between items-start gap-3">
                  <p className="font-serif text-lg text-navy">{fmtDate(i.completed_at || i.scheduled_date || i.created_at)}</p>
                  <StatusBadge status={i.status} />
                </div>
                <p className="text-sm text-body mt-1">{i.service}{i.flower_name ? ` · ${i.flower_name}` : ""}</p>
                {i.final_photo && <img src={i.final_photo} alt="Foto intervento" className="mt-3 w-full rounded-lg aspect-video object-cover" />}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
